'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCreateRouteParam } from '@/hooks/use-route-params'
import { RouteParamForm } from '@/components/partials/route-params'
import { type RouteParamFormRef } from '@/components/partials/route-params/form'
import { CreateRouteParamParams } from '@/types/route-param'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const messageFormRef = useRef<RouteParamFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createRouteParam, isPending: isLoading } = useCreateRouteParam()

  const handleSubmit = (data: CreateRouteParamParams) => {
    createRouteParam(data, {
      onSuccess: async newMessage => {
        setIsRedirecting(true)
        queryClient.setQueryData(['routeParam', newMessage.id], newMessage)
        await queryClient.invalidateQueries({ queryKey: ['routeParams'] })
        toast.success(t('created_successfully'))
        router.push(`/localization/route-params/${newMessage.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateRouteParamParams) => {
    createRouteParam(data, {
      onSuccess: async newMessage => {
        queryClient.setQueryData(['routeParam', newMessage.id], newMessage)
        await queryClient.invalidateQueries({ queryKey: ['routeParams'] })
        const detailUrl = `/localization/route-params/${newMessage.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        messageFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (messageFormRef.current) {
        messageFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/localization/route-params')
  }

  return (
    <RouteParamForm
      key={formKey}
      ref={messageFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
